import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';

import { usePetitionActionsPageData } from '#src/app/pages/actions/pageContext';
import { useVideoFirstFlow } from '#src/app/pages/actions/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { AddYourVideoButton } from '../AddYourVideoButton';
import { YourVoiceText } from '../YourVoiceText';

import { useAddSupporterVoiceThankYou } from './hooks/useAddSupporterVoiceThankYou';

export const AddSupporterVoiceThankYou: React.FC<Props> = ({ ref }) => {
	useAddSupporterVoiceThankYou();
	const {
		data: { hasRecordedVideo },
	} = useVideoFirstFlow();
	const {
		data: { petition },
	} = usePetitionActionsPageData();
	const { track } = useCampaignTracking();

	// This screen pitches recording a video after a comment; on the comment-after-video path the
	// supporter already recorded one, so it collapses to a confirmation instead. The <h1> stays either
	// way: the painted door restores focus by querying for one inside this region (CHANGE-96802).
	if (hasRecordedVideo) {
		return (
			<div ref={ref}>
				<div className="text-center" data-testid="add-supporter-voice-thank-you">
					<Text as="h1" variant="heading" size="large" fontWeight="bold" className="text-typography-strong mb-6">
						<Translate value="corgi.petition_actions.comment_after_video.heading" />
					</Text>

					<YourVoiceText />

					{/*
					 * The only way onward: the page's own skip link stands down here, so this takes its place.
					 * Goes to the recommendations rather than back to the petition, which is where this flow
					 * used to dead-end and costs the carousel its traffic (CHANGE-97687 review, 2026-08-25).
					 */}
					<Button
						as="a"
						href={`/p/${petition.slug}/d`}
						variant="primary"
						size="large"
						className="w-full"
						onClick={() => track('campaign_action_comment_after_video_continue_click')}
						data-testid="continue-after-comment"
						data-qa="continue-after-comment"
					>
						<Translate value="corgi.petition_actions.comment_after_video.continue" />
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div ref={ref}>
			<div className="text-center" data-testid="add-supporter-voice-thank-you">
				<Text as="h1" variant="heading" size="large" fontWeight="bold" className="text-typography-strong">
					<Translate value="corgi.petition_actions.supporter_voice_text.after_submit.heading" />
				</Text>

				<Text as="p" variant="body" size="medium" className="text-typography-strong my-6">
					<Translate value="corgi.petition_actions.supporter_voice_text.after_submit.call_to_action" />
				</Text>

				<YourVoiceText />

				<div className="mb-4">
					<AddYourVideoButton />
				</div>

				<Text as="p" variant="body" size="medium" className="text-typography-strong">
					<Translate value="corgi.petition_actions.supporter_voice_text.after_submit.practice_tip" />
				</Text>
			</div>
		</div>
	);
};

type Props = {
	// Lets the painted-door sequence hand focus back to this region's heading on dismissal (CHANGE-96802).
	ref?: React.Ref<HTMLDivElement>;
};
